import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
const faqs = [{
  category: 'Buying Guidance',
  questions: [{
    q: 'Which Yarbo system should I buy first?',
    a: 'Start with the task you most want to automate. If you want to automate mowing, the Yarbo Lawn Mower or Lawn Mower Pro is a strong first system. If snow clearing is your priority, the Yarbo Snow Blower is a good starting point. If you want year-round automation in a single purchase, a bundle that includes multiple modules with the Yarbo Core is the most efficient option.'
  }, {
    q: 'What is the difference between Yarbo Lawn Mower and Yarbo Lawn Mower Pro?',
    a: 'The standard Yarbo Lawn Mower handles typical lawns up to 6 acres. The Lawn Mower Pro adds dual 300W motors and 2500W peak cutting power, making it better suited for heavier grass, demanding lawn conditions, and steeper slopes.'
  }, {
    q: 'Which Yarbo bundle is best for mowing and snow?',
    a: 'The Yarbo Lawn Mower + Snow Blower bundle covers standard mowing and 2-stage snow clearing. The Yarbo Lawn Mower Pro + Snow Blower bundle adds pro-level mowing power. For year-round coverage that includes leaf blowing, the Lawn Mower Pro + Snow Blower + Blower bundle is the most complete option.'
  }]
}, {
  category: 'Modules & Compatibility',
  questions: [{
    q: 'What does "module-only" mean?',
    a: 'A module-only product is the attachment piece for a specific task (mowing, snow clearing, leaf blowing, etc.) and does not include the Yarbo Core. Module-only products are for customers who already own a compatible Yarbo Core or are purchasing the Core separately.'
  }, {
    q: 'Do modules include the Yarbo Core?',
    a: 'No. Module-only products on this site are listed in the Modules category and do not include the Yarbo Core. Complete Systems and Bundles include the Yarbo Core along with one or more modules.'
  }, {
    q: 'Can I add modules later?',
    a: 'Yes. Yarbo Y Series uses a modular architecture. You can buy a complete system today and add a different module later to expand what your Yarbo can do.'
  }]
}, {
  category: 'Ordering & Shipping',
  questions: [{
    q: 'Does Merem Robotics ship outside Michigan?',
    a: 'Yes. Merem Robotics is located in Michigan but ships nationwide across the United States.'
  }, {
    q: 'Where can I get help selecting a Yarbo product?',
    a: 'Use the Contact page to send product selection questions. We can help with which configuration best fits the tasks you want to automate.'
  }]
}, {
  category: 'YarboCare Extended Warranty FAQ',
  questions: [{
    q: 'What is YarboCare Extended Warranty?',
    a: 'YarboCare is an optional protection plan for eligible Yarbo systems. It can add 3 additional years of extended warranty coverage after the standard 2-year limited warranty.'
  }, {
    q: 'What YarboCare plans are available?',
    a: 'Available plan types may include 3-Year Extended Warranty, 1-Year Accidental Damage + 3-Year Extended Warranty, and 3-Year Accidental Damage + 3-Year Extended Warranty.'
  }, {
    q: 'What does the extended warranty cover?',
    a: 'Extended warranty coverage may include manufacturing defects, electrical or mechanical failures, performance malfunctions, and other issues covered by Yarbo\u2019s standard warranty.'
  }, {
    q: 'What does Accidental Damage protection cover?',
    a: 'Accidental Damage protection may cover eligible drops, collisions, and impact damage during normal use, depending on the selected plan.'
  }, {
    q: 'What is not covered?',
    a: 'Weather damage, liquid damage, misuse, cosmetic wear, normal wear and tear, unauthorized repairs, accessories, and standalone modules may be excluded.'
  }, {
    q: 'Which products are eligible?',
    a: 'YarboCare is shown for eligible Yarbo systems and bundles that include a Yarbo Core. Accessories and standalone modules are not presented as eligible unless final warranty rules confirm otherwise.'
  }, {
    q: 'Who provides the coverage?',
    a: 'YarboCare coverage is provided through XCotton and underwritten by AIG. Merem Robotics sells eligible Yarbo products and presents the available protection options during checkout.'
  }]
}];
export function FAQ() {
  const [openId, setOpenId] = useState<string | null>('0-0');
  return <div className="bg-white min-h-screen" data-id="element-1490">
      <div className="bg-gray-50 border-b border-gray-200" data-id="element-1491">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-1492">
          <h1 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-2" data-id="element-1493">
            Frequently Asked Questions
          </h1>
          <p className="text-gray-600" data-id="element-1494">
            Buying, modules, compatibility, and ordering.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-10" data-id="element-1495">
        {faqs.map((section, sIdx) => <div key={sIdx} data-id="element-1496">
            <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500 mb-4" data-id="element-1497">
              {section.category}
            </h2>
            <div className="space-y-3" data-id="element-1498">
              {section.questions.map((faq, qIdx) => {
            const id = `${sIdx}-${qIdx}`;
            const isOpen = openId === id;
            return <div key={qIdx} className={`border rounded-xl overflow-hidden transition-colors ${isOpen ? 'border-yarbo-dark' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1499">
                    <button onClick={() => setOpenId(isOpen ? null : id)} className="w-full text-left px-5 py-4 flex justify-between items-center" data-id="element-1500">
                      <span className="font-bold text-yarbo-dark pr-6" data-id="element-1501">
                        {faq.q}
                      </span>
                      <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180 text-yarbo-dark' : ''}`} data-id="element-1502" />
                    </button>
                    <AnimatePresence data-id="element-1503">
                      {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.2
                }} data-id="element-1504">
                          <div className="px-5 pb-5 text-gray-600 leading-relaxed" data-id="element-1505">
                            {faq.a}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>;
          })}
            </div>
          </div>)}

        <div className="bg-gray-50 rounded-2xl p-8 text-center mt-12" data-id="element-1506">
          <h3 className="text-xl font-bold text-yarbo-dark mb-2" data-id="element-1507">
            Need product selection help?
          </h3>
          <p className="text-gray-600 mb-6 text-sm" data-id="element-1508">
            Send us a message and we'll point you to the right Yarbo
            configuration.
          </p>
          <Link to="/contact" className="inline-block bg-yarbo-dark text-white font-bold px-6 py-3 rounded-lg hover:bg-black" data-id="element-1509">
            Contact Support
          </Link>
        </div>
      </div>
    </div>;
}